import React, { useState, useMemo } from "react";
import { Printer } from "lucide-react";
import { obtenerReporteFacturas } from "../services/reportesService";
import PageHeader from "../components/PageHeader";
import DataTable from "../components/DataTable";
import Toast, { useToast } from "../components/Toast";

const today = new Date();
const toHtmlDate = (d) => d.toISOString().slice(0, 10);
const DEFAULT_FECHA_INI = toHtmlDate(new Date(today.getFullYear(), today.getMonth(), 1));
const DEFAULT_FECHA_FIN = toHtmlDate(new Date(today.getFullYear(), today.getMonth() + 1, 0));

const htmlToApiDate = (d) => (d ? d.replace(/-/g, "/") : "");

const displayDate = (d) => {
  if (!d) return "";
  const [y, m, day] = d.split("/");
  return `${day}/${m}/${y}`;
};

const fmtCurrency = (n) =>
  `$${(Number(n) || 0).toLocaleString("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// ── Componente solo para impresión ──────────────────────────────────────────
function PrintView({ filas, fechaInicial, fechaFinal, moneda, totales }) {
  return (
    <div style={{ padding: 24, fontFamily: "Arial, sans-serif", fontSize: 11, color: "#000" }}>
      <div style={{ textAlign: "center", marginBottom: 16 }}>
        <div style={{ fontSize: 18, fontWeight: "bold" }}>LMD Integradora</div>
        <div style={{ fontSize: 14, marginTop: 4 }}>Reporte de Facturacion de</div>
        <div style={{ fontSize: 14, fontWeight: "bold", marginTop: 4 }}>
          {displayDate(fechaInicial)}&nbsp;&nbsp;AL&nbsp;&nbsp;{displayDate(fechaFinal)}&nbsp;&nbsp;En&nbsp;&nbsp;{moneda === "P" ? "Pesos" : "Dolares"}
        </div>
      </div>

      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11 }}>
        <thead>
          <tr style={{ background: "#e8e8e8" }}>
            <th style={thStyle}>Factura</th>
            <th style={thStyle}>Fecha</th>
            <th style={{ ...thStyle, textAlign: "left" }}>Nombre</th>
            <th style={{ ...thStyle, textAlign: "right" }}>Subtotal</th>
            <th style={{ ...thStyle, textAlign: "right" }}>IVA</th>
            <th style={{ ...thStyle, textAlign: "right" }}>Total</th>
            <th style={thStyle}>Estatus</th>
          </tr>
        </thead>
        <tbody>
          {filas.map((f, i) => (
            <tr key={f.factura} style={{ background: i % 2 === 0 ? "#fff" : "#f9f9f9", borderBottom: "1px solid #ddd" }}>
              <td style={tdStyle}>{f.factura}</td>
              <td style={tdStyle}>{displayDate(f.fecha)}</td>
              <td style={{ ...tdStyle, textAlign: "left" }}>{f.nombre}</td>
              <td style={{ ...tdStyle, textAlign: "right" }}>{fmtCurrency(f.subtotal)}</td>
              <td style={{ ...tdStyle, textAlign: "right" }}>{fmtCurrency(f.iva)}</td>
              <td style={{ ...tdStyle, textAlign: "right" }}>{fmtCurrency(f.total)}</td>
              <td style={tdStyle}>{f.estatus}</td>
            </tr>
          ))}
          <tr style={{ borderTop: "2px solid #333", fontWeight: "bold" }}>
            <td colSpan={3} style={{ ...tdStyle, textAlign: "left", fontWeight: "bold", paddingTop: 8 }}>T O T A L E S</td>
            <td style={{ ...tdStyle, textAlign: "right", paddingTop: 8 }}>{fmtCurrency(totales.subtotal)}</td>
            <td style={{ ...tdStyle, textAlign: "right", paddingTop: 8 }}>{fmtCurrency(totales.iva)}</td>
            <td style={{ ...tdStyle, textAlign: "right", paddingTop: 8 }}>{fmtCurrency(totales.total)}</td>
            <td style={tdStyle} />
          </tr>
        </tbody>
      </table>
    </div>
  );
}

const thStyle = { padding: "5px 8px", textAlign: "center", borderBottom: "2px solid #333" };
const tdStyle  = { padding: "3px 8px", textAlign: "center" };

// ── Página principal ─────────────────────────────────────────────────────────
export default function ReporteFacturasPage() {
  const [filters, setFilters] = useState({
    fechaInicial: DEFAULT_FECHA_INI,
    fechaFinal: DEFAULT_FECHA_FIN,
    moneda: "P",
    cliente: "",
  });
  const [filas, setFilas]     = useState(null);
  const [loading, setLoading] = useState(false);
  const [consultaFechas, setConsultaFechas] = useState({ ini: "", fin: "", moneda: "P" });

  const empresaId = Number(localStorage.getItem("empresaId") || 1);
  const { toasts, closeToast, error: toastError } = useToast();

  const handlePrint = () => window.print();

  const onChange = (e) => {
    const { name, value } = e.target;
    setFilters((f) => ({ ...f, [name]: value }));
  };

  const handleConsultar = async () => {
    setLoading(true);
    setFilas(null);
    try {
      const fechaInicial = htmlToApiDate(filters.fechaInicial);
      const fechaFinal   = htmlToApiDate(filters.fechaFinal);
      const data = await obtenerReporteFacturas({
        fechaInicial,
        fechaFinal,
        moneda: filters.moneda,
        empresaId,
        cliente: filters.cliente,
      });
      setFilas(data || []);
      setConsultaFechas({ ini: fechaInicial, fin: fechaFinal, moneda: filters.moneda });
    } catch {
      toastError("Error al consultar el reporte de facturas.");
    } finally {
      setLoading(false);
    }
  };

  const totales = useMemo(() => {
    if (!filas) return { subtotal: 0, iva: 0, total: 0 };
    return filas.reduce(
      (acc, f) => ({
        subtotal: acc.subtotal + (Number(f.subtotal) || 0),
        iva:      acc.iva      + (Number(f.iva)      || 0),
        total:    acc.total    + (Number(f.total)     || 0),
      }),
      { subtotal: 0, iva: 0, total: 0 }
    );
  }, [filas]);

  const columns = [
    { key: "factura",  title: "Factura", style: { width: 90 } },
    { key: "fecha",    title: "Fecha",   style: { width: 110 }, render: (r) => displayDate(r.fecha) },
    { key: "nombre",   title: "Nombre" },
    { key: "subtotal", title: "Subtotal", style: { width: 130 }, className: "num", render: (r) => fmtCurrency(r.subtotal) },
    { key: "iva",      title: "IVA",     style: { width: 110 }, className: "num", render: (r) => fmtCurrency(r.iva) },
    { key: "total",    title: "Total",   style: { width: 130 }, className: "num", render: (r) => fmtCurrency(r.total) },
    {
      key: "estatus", title: "Estatus", style: { width: 100 },
      render: (r) => {
        const cancelada = r.estatus === "Cancelada";
        return (
          <span style={{
            fontSize: "var(--fs-xs)",
            padding: "2px 10px",
            borderRadius: "var(--radius-full, 9999px)",
            fontWeight: "var(--fw-medium)",
            background: cancelada ? "var(--clr-warning-bg, #FFF3CD)" : "var(--clr-success-bg, #DCFCE7)",
            color:      cancelada ? "var(--clr-warning,    #92400E)" : "var(--clr-success,    #065F46)",
          }}>
            {r.estatus}
          </span>
        );
      },
    },
  ];

  return (
    <>
      <Toast toasts={toasts} onClose={closeToast} />
      <PageHeader title="Relación de Facturas" subtitle="Reporte de facturación por período" />

      {/* Filtros */}
      <div className="lmd-card reporte-no-print" style={{ marginBottom: "var(--sp-4)" }}>
        <div className="lmd-card-header">
          <h3 className="lmd-card-title">Filtros</h3>
        </div>
        <div className="lmd-card-body">
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: "var(--sp-4)", alignItems: "end" }}>
            <div className="lmd-field">
              <label className="lmd-label">Fecha Inicial</label>
              <input className="lmd-input" type="date" name="fechaInicial" value={filters.fechaInicial} onChange={onChange} />
            </div>
            <div className="lmd-field">
              <label className="lmd-label">Fecha Final</label>
              <input className="lmd-input" type="date" name="fechaFinal" value={filters.fechaFinal} onChange={onChange} />
            </div>
            <div className="lmd-field">
              <label className="lmd-label">Moneda</label>
              <select className="lmd-select" name="moneda" value={filters.moneda} onChange={onChange} style={{ width: "100%" }}>
                <option value="P">Pesos (MXN)</option>
                <option value="D">Dólares (USD)</option>
              </select>
            </div>
            <div className="lmd-field">
              <label className="lmd-label">Cliente (opcional)</label>
              <input className="lmd-input" name="cliente" value={filters.cliente} onChange={onChange} placeholder="Clave de cliente..." />
            </div>
            <div className="lmd-field">
              <label className="lmd-label">&nbsp;</label>
              <button
                className="lmd-btn lmd-btn-primary"
                onClick={handleConsultar}
                disabled={loading}
                style={{ width: "100%" }}
              >
                {loading ? "Consultando…" : "Consultar"}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Resultados */}
      {filas !== null && (
        <div className="lmd-card reporte-no-print">
          <div className="lmd-card-header">
            <h3 className="lmd-card-title">
              Resultados&nbsp;
              <span style={{ fontWeight: "normal", color: "var(--clr-text-muted)", fontSize: "var(--fs-sm)" }}>
                {filas.length} facturas
              </span>
            </h3>
            <button
              className="lmd-btn lmd-btn-secondary"
              onClick={handlePrint}
              disabled={filas.length === 0}
              style={{ display: "flex", alignItems: "center", gap: 6 }}
            >
              <Printer size={15} /> Imprimir
            </button>
          </div>

          {filas.length === 0 ? (
            <div style={{ padding: "32px", textAlign: "center", color: "var(--clr-text-muted)" }}>
              No se encontraron facturas para el período seleccionado.
            </div>
          ) : (
            <>
              <DataTable columns={columns} rows={filas} keyField="factura" />

              {/* Totales */}
              <div style={{
                display: "flex",
                gap: "var(--sp-6)",
                justifyContent: "flex-end",
                padding: "12px 20px",
                borderTop: "2px solid var(--clr-border)",
                fontWeight: "var(--fw-bold)",
                fontSize: "var(--fs-sm)",
              }}>
                <span>Subtotal: {fmtCurrency(totales.subtotal)}</span>
                <span>IVA: {fmtCurrency(totales.iva)}</span>
                <span style={{ fontSize: "var(--fs-md)", color: "var(--clr-primary)" }}>
                  Total: {fmtCurrency(totales.total)}
                </span>
              </div>
            </>
          )}
        </div>
      )}

      {/* Vista de impresión */}
      <div className="reporte-solo-print">
        <PrintView
          filas={filas || []}
          fechaInicial={consultaFechas.ini}
          fechaFinal={consultaFechas.fin}
          moneda={consultaFechas.moneda}
          totales={totales}
        />
      </div>

      <style>{`
        @media screen { .reporte-solo-print { display: none; } }
        @media print  {
          .reporte-no-print { display: none !important; }
          .reporte-solo-print { display: block !important; }
        }
      `}</style>
    </>
  );
}
